'use client';

import { type FocusEventHandler, type ReactElement, type Ref, useEffect, useMemo, useRef, useState } from 'react';
import { type CountryIso2, defaultCountries, parseCountry, usePhoneInput } from 'react-international-phone';
import { ChevronDown } from 'lucide-react';

import { classNames } from '@m1work/shared-utils';

import { Icon } from '../Icon/Icon';
import { getCountryName } from '../utils/getCountryName';
import { getFlagEmoji } from '../utils/getFlagEmoji';
import { mergeRefs } from '../utils/mergeRefs';

import styles from './PhoneInput.module.scss';

export interface PhoneInputProps {
    /** Phone in international format, e.g. `+34600000000` (controlled). @default '' */
    value?: string;
    /** Called with the phone in international format. */
    onChange?: (phone: string) => void;
    /** Country selected while the value is empty. @default 'es' */
    defaultCountry?: CountryIso2;
    /** @default false */
    disabled?: boolean;
    /** Show the error state (red border). @default false */
    error?: boolean;
    className?: string;
    placeholder?: string;
    /** Language of the country names in the list, e.g. `es`; English names of the library when not set. */
    locale?: string;
    /** `id` of the number field, for a `<label htmlFor>` (`FormGroup labelFor`). */
    id?: string;
    /** `name` of the number field. */
    name?: string;
    /** Called when the number field loses focus (e.g. react-hook-form's `field.onBlur`). */
    onBlur?: FocusEventHandler<HTMLInputElement>;
    /** Number can't be edited, the country can't be changed; still focusable and submitted. @default false */
    readOnly?: boolean;
    /** Browser autofill hint of the number field. @default 'tel' */
    autoComplete?: string;
    /** Ref to the number field, e.g. to focus it. */
    ref?: Ref<HTMLInputElement>;
    /** Accessible name of the country button, e.g. "Select country code" (translated). */
    countryButtonLabel: string;
    /** Accessible name of the country list, e.g. "Country codes" (translated). */
    countryListLabel: string;
}

const COUNTRIES = defaultCountries.map(parseCountry);

/**
 * A phone field with a country picker (flag and dial code) that formats the number as it's typed.
 *
 * @example
 * <PhoneInput
 *     value={field.value}
 *     onChange={field.onChange}
 *     locale={i18n.language}
 *     countryButtonLabel={t('form.phone.country-button')}
 *     countryListLabel={t('form.phone.country-list')}
 * />
 */
export function PhoneInput({
    value = '',
    onChange,
    defaultCountry = 'es',
    disabled = false,
    error = false,
    className,
    placeholder,
    locale,
    id,
    name,
    onBlur,
    readOnly = false,
    autoComplete = 'tel',
    ref,
    countryButtonLabel,
    countryListLabel,
}: PhoneInputProps): ReactElement {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
        defaultCountry,
        value,
        countries: defaultCountries,
        onChange: ({ phone }) => onChange?.(phone),
    });

    const countryOptions = useMemo(
        () =>
            COUNTRIES.map((option) => ({ ...option, name: getCountryName(option.iso2, locale, option.name) })).sort(
                (a, b) => a.name.localeCompare(b.name, locale),
            ),
        [locale],
    );

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleOutsideClick = (e: MouseEvent): void => {
            if (e.target instanceof Node && !containerRef.current?.contains(e.target)) {
                setIsOpen(false);
            }
        };

        const handleEscape = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') {
                // Only the list closes: a dialog around it (Radix) listens on `document` in the capture phase,
                // and `window` capture runs before it.
                e.stopPropagation();
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        window.addEventListener('keydown', handleEscape, { capture: true });

        return (): void => {
            document.removeEventListener('mousedown', handleOutsideClick);
            window.removeEventListener('keydown', handleEscape, { capture: true });
        };
    }, [isOpen]);

    const handleSelect = (iso2: CountryIso2): void => {
        setCountry(iso2);
        setIsOpen(false);
    };

    return (
        <div ref={containerRef} className={classNames(styles.container, disabled && styles.disabled, className)}>
            <div className={classNames(styles.wrapper, error && styles.error)}>
                <button
                    type="button"
                    className={styles.trigger}
                    onClick={() => setIsOpen((prev) => !prev)}
                    disabled={disabled || readOnly}
                    aria-label={countryButtonLabel}
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                >
                    <span className={styles.flag}>{getFlagEmoji(country.iso2)}</span>
                    <Icon
                        icon={ChevronDown}
                        size="xs"
                        className={classNames(styles['trigger-icon'], isOpen && styles['is-open'])}
                    />
                </button>

                <span className={classNames(styles.divider, 'my-2')} aria-hidden="true" />

                <input
                    ref={mergeRefs(inputRef, ref)}
                    id={id}
                    name={name}
                    type="tel"
                    autoComplete={autoComplete}
                    readOnly={readOnly}
                    onBlur={onBlur}
                    className={styles.input}
                    value={inputValue}
                    onChange={handlePhoneValueChange}
                    disabled={disabled}
                    placeholder={placeholder}
                />
            </div>

            {isOpen && (
                <div className={styles.dropdown} role="listbox" aria-label={countryListLabel}>
                    {countryOptions.map((option) => (
                        <button
                            key={option.iso2}
                            type="button"
                            role="option"
                            aria-selected={option.iso2 === country.iso2}
                            className={classNames(styles.item, option.iso2 === country.iso2 && styles['is-selected'])}
                            onClick={() => handleSelect(option.iso2)}
                        >
                            <span className={styles['item-flag']}>{getFlagEmoji(option.iso2)}</span>
                            <span className={styles['item-name']}>{option.name}</span>
                            <span className={styles['item-dial']}>(+{option.dialCode})</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
